"use client";

import type React from "react";
import { motion, useReducedMotion } from "@/lib/motion";
import { cn } from "@/lib/utils";

interface FeatureCardProps {
  title: string;
  description: string;
  /** Ambient looping visual rendered above the title (see products/shared/feature-visuals). */
  Visual?: React.ComponentType;
  /** Drop the tile chrome (background, padding) — keep the text layout only. */
  bare?: boolean;
  /** Bleed the accent pair in from the card's corners (`.card-wash` in
   *  globals.css) so a grid of tiles isn't flat white on the light theme. The
   *  hue and corner rotate every third sibling, so turn it on for a whole grid
   *  rather than a single card — otherwise the rotation has nothing to cycle.
   *  Pass `"blue"` for the blue-only variant, which drops the warm corner: on a
   *  big dense grid the orange stops reading as a hint and starts reading as a
   *  second kind of card. */
  wash?: boolean | "blue";
  /** Seat the visual in a recessed panel instead of letting it float on the card
   *  surface. Gives the looping animation a frame to run in, so it reads as a
   *  product view rather than loose chips, and separates it from the copy. */
  visualWell?: boolean;
  /** Extra classes on the tile — used to opt into shared behaviour like
   *  `.card-hover`. */
  className?: string;
  /** Let the content rise in on its own beat. Turn OFF where the caller already
   *  animates the whole tile (see products/shared/feature-grid): two reveals on
   *  one card read as the shell arriving first and the text catching up. */
  revealContent?: boolean;
}

/* The reveal: a short rise with a hair of scale on a decelerating curve, which
   lands far crisper than a linear fade. The same ease the dashboard tabs and the
   hero CTA use, so every entrance on the page shares one motion character. */
const variants = {
  hidden: { opacity: 0, y: 18, scale: 0.985 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

const staticVariants = {
  hidden: { opacity: 1, y: 0, scale: 1 },
  visible: { opacity: 1, y: 0, scale: 1 },
};

/** Icon-less feature tile. Carries its own surface, border and radius, so it
    sits in a plain gapped grid (`grid gap-4 sm:gap-5`) rather than the hairline
    `gap-px` container it used to need. Only the inner content animates in. */
export default function FeatureCard({
  title,
  description,
  Visual,
  bare = false,
  wash = false,
  visualWell = false,
  className,
  revealContent = true,
}: FeatureCardProps) {
  const prefersReducedMotion = useReducedMotion();
  const animateContent = revealContent && !prefersReducedMotion;

  return (
    <div
      className={cn(
        "group flex flex-col",
        !bare && "bg-card border-border rounded-2xl border p-6",
        // The wash rides on the tile's own surface, so it needs that chrome.
        !bare && wash && "relative overflow-hidden",
        !bare && wash && (wash === "blue" ? "card-wash-blue" : "card-wash"),
        className
      )}
    >
      {/* Positioned so the content sits above any decorative pseudo-element the
          tile opts into — `.card-spotlight` paints at z-index 0. Harmless for
          tiles that use neither. */}
      <motion.div variants={animateContent ? variants : staticVariants} className="relative z-10">
        {Visual && (
          <div
            className={cn(
              "mb-5",
              visualWell &&
                "border-border/60 bg-foreground/3 group-hover:border-border rounded-xl border transition-colors"
            )}
          >
            <Visual />
          </div>
        )}
        <h3 className="text-foreground text-[15px] font-semibold tracking-[-0.01em]">{title}</h3>
        <p className="text-muted-foreground mt-1.5 text-sm leading-relaxed">{description}</p>
      </motion.div>
    </div>
  );
}
